import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ReportStatus, FilingOrigin, PreflightCopy, AmendmentModel } from '@signalvote/shared';
import type { ComplianceReportDto, ComplianceReportListParams } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useCampaignStore } from '@/stores';
import { useFinanceReports, useAmendReport, useFilingChain } from '@/hooks/queries/useFinanceReports';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { formatAmount, formatDate } from '../utils';
import { statusBadge, lockBadge, openAmendment } from './reportUtils';
import { FilingLockCopy, externalLabelTooltip } from './filingLockCopy';
import { NewReportingPeriodDialog } from './NewReportingPeriodDialog';
import { portalNameFor } from './amendmentWorkflowHint';
import { withReceivingOffice } from './receivingOffice';

// ============================================================================
// Component
// ============================================================================

export function ReportListPage() {
  const navigate = useNavigate();
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const { amendReport } = useAmendReport();
  // #571 — the amend UI reads the ONE served amendment axis (ADR-021 `amendmentModel`), never a
  // `stateCode === 'AL'` boolean or a FormatKind derivation (the Finance golden rule forbids the state
  // boolean). PortalOnly (AL) = amend in the append-only FCPA portal (edit/delete, never re-upload —
  // ADR-020); SelfContainedUpload (LA/VA) = the app rebuilds from the ledger and you re-upload;
  // ElectronicResubmit (federal) = the app files the amendment in-system.
  const { data: profile } = useComplianceProfile();
  const amendmentModel = profile?.amendmentModel ?? null;
  // §34 — the app does NOT perform the amendment for a portal jurisdiction (AL's append-only portal OR
  // LA/VA's rebuild-and-re-upload); the treasurer completes it in the portal, so the Amend button + CTA
  // say so plainly. Federal (ElectronicResubmit) and an unresolved model keep the plain in-system
  // "Amend" affordance (generate = file for federal).
  const amendsInPortal =
    amendmentModel === AmendmentModel.PortalOnly ||
    amendmentModel === AmendmentModel.SelfContainedUpload;
  // NC is SelfContainedUpload but files by EMAIL to NCSBE (no portal). The amend copy branches on
  // the served transport axis (`filingTransport === 'email'`), never a `stateCode === 'NC'` boolean.
  // Only SelfContainedUpload co-occurs with email today; portal-edit (AL) and in-system (federal)
  // have no email variant.
  const isEmailFiling = profile?.filingTransport === 'email';
  // The email copy names the SERVED receiving office (NC → "NCSBE"; MS → "the Secretary of State").
  const emailOffice = (copy: string) =>
    withReceivingOffice(copy, profile?.portalName ?? portalNameFor(profile?.stateCode));
  // The Amend affordance copy, resolved once so the row button, modal CTA, and modal body can't
  // drift: portal jurisdictions (AL/LA/VA) say "in portal"; NC (email) says "emailed to NCSBE";
  // federal / unresolved keep the plain in-system "Amend" / "Confirm Amendment".
  const amendButtonLabel = !amendsInPortal
    ? 'Amend'
    : isEmailFiling
      ? emailOffice(PreflightCopy.RecordAmendmentEmailButtonLabel)
      : PreflightCopy.RecordAmendmentButtonLabel;
  const amendConfirmCta = !amendsInPortal
    ? 'Confirm Amendment'
    : isEmailFiling
      ? emailOffice(PreflightCopy.RecordAmendmentEmailModalCta)
      : PreflightCopy.RecordAmendmentModalCta;
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Amend confirmation state
  const [amendConfirm, setAmendConfirm] = useState<{ reportId: string; concurrencyVersion: number } | null>(null);
  const amendConfirmId = amendConfirm?.reportId ?? null;
  const [isAmending, setIsAmending] = useState(false);
  const [amendError, setAmendError] = useState<string | null>(null);
  const { data: amendChain } = useFilingChain(amendConfirmId);
  const inProgress = openAmendment(amendChain?.chain);

  const params: ComplianceReportListParams = {
    page,
    pageSize: 20,
    ...(statusFilter ? { status: statusFilter } : {}),
  };

  const { data, isLoading, isError } = useFinanceReports(params);

  if (isLoading) {
    return <div className="p-6 text-center text-muted-foreground">Loading...</div>;
  }

  if (isError) {
    return <div className="p-6 text-center text-red-600">Failed to load reports. Please try again.</div>;
  }

  const reports = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const pageSize = data?.pageSize ?? 20;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  // ADR-018 (S3) — an externally-filed report on the list means a backfill ran; surface
  // the bounded onboarding caveat. Honest + bounded: the missing amounts are all
  // individually sub-threshold, so we disclose the small undercount, never claim
  // perfect per-donor completeness.
  const hasExternallyFiled = reports.some((r) => r.filingOrigin === FilingOrigin.ExternalPortal);

  const handleAmendConfirm = async () => {
    if (!amendConfirm) return;
    setIsAmending(true);
    setAmendError(null);
    try {
      const reportId = await amendReport(amendConfirm);
      setAmendConfirm(null);
      navigate({ to: '/admin/finance/reports/$reportId', params: { reportId } });
    } catch (err) {
      setAmendError(err instanceof Error ? err.message : 'Failed to amend report');
    } finally {
      setIsAmending(false);
    }
  };

  if (reports.length === 0 && !statusFilter) {
    return (
      <div className="flex flex-col items-center gap-3 p-6 text-center text-muted-foreground">
        <div>
          <p>No reports generated yet.</p>
          <p className="text-sm mt-1">Reports are prepared and filed from Close the period.</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={() => navigate({ to: '/admin/finance/close' })}>
            Go to Close the period
          </Button>
          {canManage && <NewReportingPeriodDialog />}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hasExternallyFiled && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"
          data-testid="imported-history-caveat"
        >
          <p className="font-medium">{FilingLockCopy.ImportedHistoryCaveatHeading}</p>
          <p className="mt-1 text-blue-800">{FilingLockCopy.ImportedHistoryCaveat}</p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Reports</h2>
        <div className="flex items-center gap-2">
          {/* #132 — add a reporting period by hand for a filing the calendar doesn't
              auto-generate (e.g. VA's election-relative periods). Finance.Manage only. */}
          {canManage && <NewReportingPeriodDialog />}
          <label htmlFor="report-status-filter" className="sr-only">Status</label>
          <select
            id="report-status-filter"
            aria-label="Status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-md border px-3 py-1.5 text-sm"
          >
            <option value="">All statuses</option>
            {Object.values(ReportStatus).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="pb-2 pr-4 font-medium">Name</th>
              <th className="pb-2 pr-4 font-medium">Period</th>
              <th className="pb-2 pr-4 font-medium">Status</th>
              <th className="pb-2 pr-4 font-medium text-right">Contributions</th>
              <th className="pb-2 pr-4 font-medium text-right">Expenditures</th>
              <th className="pb-2 pr-4 font-medium text-right">Cash on Hand</th>
              <th className="pb-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <ReportRow
                key={report.id}
                report={report}
                canManage={canManage}
                onView={() =>
                  navigate({
                    to: '/admin/finance/reports/$reportId',
                    params: { reportId: report.id },
                  })
                }
                onFile={() =>
                  // FIN-011: filing happens on the report detail page via the
                  // new submission-package flow. Navigate there instead of
                  // opening the legacy confirmation-number dialog.
                  navigate({
                    to: '/admin/finance/reports/$reportId',
                    params: { reportId: report.id },
                  })
                }
                onAmend={() => setAmendConfirm({ reportId: report.id, concurrencyVersion: report.concurrencyVersion })}
                amendButtonLabel={amendButtonLabel}
                stateDisplayName={profile?.stateDisplayName ?? null}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-md border p-1 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded-md border p-1 disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Amend Confirmation Dialog */}
      {amendConfirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
            <h3 className="text-lg font-semibold mb-2">Amend Report</h3>
            {/* #571 — the dialog body is a pure function of the served `amendmentModel` (ADR-021): the
                three declared models map 1:1 to the three amendment stories, so federal
                (ElectronicResubmit) and LA/VA (SelfContainedUpload) are now distinguishable here
                (they used to collapse). No `stateCode === 'AL'` boolean, no FormatKind derivation. */}
            {inProgress ? (
              <p className="text-sm text-muted-foreground mb-4">
                Amendment #{inProgress.amendmentNumber} is already in progress. Continue it instead of
                starting another.
              </p>
            ) : amendmentModel === AmendmentModel.PortalOnly ? (
              // AL (PortalOnly) amendments are portal-only edits/deletes: the FCPA portal is
              // append-only (ADR-020) so re-uploading the full file would duplicate every row. Single
              // source of truth for this copy is PreflightCopy. Alert-don't-block — Confirm never
              // suppressed.
              <p className="text-sm text-muted-foreground mb-4">
                {PreflightCopy.AlabamaAmendmentPortalWorkflowBody}
              </p>
            ) : amendmentModel === AmendmentModel.SelfContainedUpload ? (
              // Issue #219 — self-contained-upload jurisdictions rebuild the report from the current
              // ledger (add forgotten donations first), the treasurer files the new file, and the
              // original is marked Amended once the correction is filed (ADR-050). Portal filers
              // (LA/VA) upload it; NC files by EMAIL to NCSBE — so the body branches on the served
              // transport, matching the email-vs-portal CTA. Plain-steps copy either way.
              <p className="text-sm text-muted-foreground mb-4">
                {isEmailFiling
                  ? emailOffice(PreflightCopy.RecordAmendmentEmailBody)
                  : PreflightCopy.RecordAmendmentPortalUploadBody}
              </p>
            ) : (
              // In-system (federal, ElectronicResubmit) or an unresolved model: the app files the
              // amendment directly, and the original flips to Amended in the same save
              // (AmendmentChainMarker, ADR-050).
              <p className="text-sm text-muted-foreground mb-4">
                This will generate an amended report with updated financials. The original will be marked as Amended.
              </p>
            )}
            {amendError && (
              <div className="mb-4 rounded-md bg-red-50 p-2 text-sm text-red-700">{amendError}</div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setAmendConfirm(null); setAmendError(null); }}
                className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                Cancel
              </button>
              {inProgress ? (
                <button
                  onClick={() => {
                    setAmendConfirm(null);
                    navigate({
                      to: '/admin/finance/reports/$reportId',
                      params: { reportId: inProgress.complianceReportId },
                    });
                  }}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Continue Amendment #{inProgress.amendmentNumber}
                </button>
              ) : (
                <button
                  onClick={handleAmendConfirm}
                  disabled={isAmending}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {isAmending ? 'Amending...' : amendConfirmCta}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Report Row
// ============================================================================

function ReportRow({
  report,
  canManage,
  onView,
  onFile,
  onAmend,
  amendButtonLabel,
  stateDisplayName,
}: {
  report: ComplianceReportDto;
  canManage: boolean;
  onView: () => void;
  onFile: () => void;
  onAmend: () => void;
  // §34/#571 — the resolved Amend button copy. Portal jurisdictions (AL PortalOnly + LA/VA
  // SelfContainedUpload) relabel it to make clear the app doesn't file the amendment; NC (email)
  // says "emailed to NCSBE"; federal (ElectronicResubmit) keeps the plain "Amend". Resolved by the
  // parent (where the served transport axis lives) so the row label can't drift from the modal CTA.
  amendButtonLabel: string;
  // ADR-018 — the externally-filed tooltip names the campaign's own filing authority
  // (null = jurisdiction-neutral); never a hardcoded "AL SOS".
  stateDisplayName: string | null;
}) {
  const navigate = useNavigate();
  const badge = statusBadge(report);
  // ADR-019 — read the computed lock chip from the DTO (never re-derive the calendar
  // predicate). ADR-018 — an externally-filed report shows the AL SOS label in place of
  // the in-system "Filed" chrome, and has no in-product amend (amendment is portal-side).
  const lock = lockBadge(report);
  const isExternallyFiled = report.filingOrigin === FilingOrigin.ExternalPortal;

  const isReady = report.status === ReportStatus.Ready;
  const isFileable = isReady;
  const isAmendable =
    !isExternallyFiled &&
    (report.status === ReportStatus.Filed || report.status === ReportStatus.NeedsAmendment);

  return (
    <tr className="border-b last:border-0">
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <span className="font-medium">{report.name}</span>
          {report.amendmentNumber > 0 && (
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
              Amendment #{report.amendmentNumber}
            </span>
          )}
          {report.originalReportId && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate({ to: '/admin/finance/reports/$reportId', params: { reportId: report.originalReportId! } }); }}
              className="text-xs text-primary underline"
            >
              View original
            </button>
          )}
        </div>
      </td>
      <td className="py-3 pr-4 text-muted-foreground">
        {formatDate(report.periodStart)} – {formatDate(report.periodEnd)}
      </td>
      <td className="py-3 pr-4">
        <div className="flex flex-wrap items-center gap-2">
          {isExternallyFiled ? (
            <span
              className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
              data-testid={`external-filed-label-${report.id}`}
              title={externalLabelTooltip(stateDisplayName)}
            >
              {FilingLockCopy.ExternalLabel}
            </span>
          ) : (
            <span
              className={cn('rounded-full px-2 py-0.5 text-xs font-medium', badge.className)}
              data-testid={`status-badge-${report.id}`}
            >
              {badge.label}
            </span>
          )}
          {lock && (
            <span
              className={cn('rounded-full px-2 py-0.5 text-xs font-medium', lock.className)}
              data-testid={`lock-badge-${report.id}`}
              title={lock.tooltip}
            >
              {lock.label}
            </span>
          )}
          {/* GitHub #45 — rows that were still Pending when this report was filed
              (or would not file right now on a Ready report). Informational, not a
              current blocker, so a quiet yellow. Hidden at zero. */}
          {report.pendingAtFilingCount > 0 && (
            <span
              className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800"
              data-testid={`pending-at-filing-badge-${report.id}`}
              title="Transactions that were still pending when this report was filed and were not included in the filing"
            >
              {report.pendingAtFilingCount} pending at filing
            </span>
          )}
          {/* GitHub #45 — open portal actions still needed for this filing (issue #34
              PortalFollowUp checklist). Amber, mirroring the detail-page checklist.
              Hidden at zero. */}
          {report.openFollowUpCount > 0 && (
            <span
              className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
              data-testid={`portal-followup-badge-${report.id}`}
              title="Portal actions still needed for this filing"
            >
              {report.openFollowUpCount}{' '}
              {report.openFollowUpCount === 1 ? 'portal follow-up' : 'portal follow-ups'}
            </span>
          )}
          {report.portalRunNeedsYou && (
            <span
              className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
              data-testid={`portal-run-badge-${report.id}`}
              title="A portal run you started needs you or has finished"
            >
              Portal run needs you
            </span>
          )}
        </div>
      </td>
      <td className="py-3 pr-4 text-right">{formatAmount(report.totalContributions)}</td>
      <td className="py-3 pr-4 text-right">{formatAmount(report.totalExpenditures)}</td>
      <td className="py-3 pr-4 text-right">{formatAmount(report.cashOnHand)}</td>
      <td className="py-3">
        <div className="flex gap-2">
          <button
            onClick={onView}
            className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-accent"
          >
            View
          </button>
          {canManage && isFileable && (
            <button
              data-testid={`file-btn-${report.id}`}
              onClick={onFile}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              File
            </button>
          )}
          {canManage && isAmendable && (
            <button
              data-testid={`amend-btn-${report.id}`}
              onClick={onAmend}
              className="rounded-md border border-orange-300 px-3 py-1 text-xs font-medium text-orange-700 hover:bg-orange-50"
            >
              {amendButtonLabel}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
